import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../services/api";
import { useAuthStore } from "../stores/authStore";
import { useFamilyStore } from "../stores/familyStore";
import type { Provider, StoreItem } from "@starquest/core";
import styles from "./KidHome.module.css";
import own from "./KidStore.module.css";
import PreviewBanner from "../components/PreviewBanner";
import ThemeWrapper from "../components/ThemeWrapper";
import { useTheme } from "../hooks/useTheme";

export default function KidStore() {
  const navigate   = useNavigate();
  const { familyId: authFamilyId } = useAuthStore();
  const { selectedKid, providers, storeItems, wishlistItemIds, setProviders, setStoreItems, setWishlistItemIds } = useFamilyStore();
  const kid = selectedKid;
  const familyId = authFamilyId || kid?.familyId || null;
  const theme = useTheme(selectedKid);

  const [loading,       setLoading]       = useState(true);
  const [loadError,     setLoadError]     = useState(false);
  const [requestItem,   setRequestItem]   = useState<StoreItem | null>(null);
  const [requesting,    setRequesting]    = useState(false);
  const [requestDone,   setRequestDone]   = useState<StoreItem | null>(null);
  const [showWishModal, setShowWishModal] = useState(false);
  const [wishName,      setWishName]      = useState("");
  const [wishUrl,       setWishUrl]       = useState("");
  const [wishBusy,      setWishBusy]      = useState(false);
  const [wishDone,      setWishDone]      = useState(false);

  useEffect(() => {
    if (!selectedKid) navigate("/kid");
  }, [selectedKid, navigate]);

  useEffect(() => {
    if (!familyId) return;
    setLoading(true);
    const calls: Promise<void>[] = [
      api.listProviders(familyId).then((p) => setProviders(p as Provider[])),
      api.listItems(familyId).then((i) => setStoreItems(i as StoreItem[])),
    ];
    if (kid) {
      calls.push(
        api.getWishlist(familyId, kid.kidId).then((w) =>
          setWishlistItemIds((w as { itemIds: string[] }).itemIds)
        )
      );
    }
    Promise.all(calls)
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [familyId, kid?.kidId]);

  async function toggleHeart(itemId: string) {
    if (!familyId || !kid) return;
    const result = await api.toggleWishlist(familyId, kid.kidId, itemId) as { hearted: boolean };
    setWishlistItemIds(
      result.hearted
        ? [...wishlistItemIds, itemId]
        : wishlistItemIds.filter((id) => id !== itemId)
    );
  }

  async function submitRequest() {
    if (!familyId || !kid || !requestItem) return;
    setRequesting(true);
    try {
      await api.createRedemption(familyId, { kidId: kid.kidId, itemId: requestItem.itemId });
      setRequestDone(requestItem);
      setRequestItem(null);
    } catch {}
    finally { setRequesting(false); }
  }

  async function submitWish() {
    if (!familyId || !kid || !wishName.trim()) return;
    setWishBusy(true);
    try {
      await api.createWish(familyId, { kidId: kid.kidId, name: wishName.trim(), url: wishUrl.trim() || undefined });
      setWishDone(true);
      setWishName(""); setWishUrl("");
    } catch {}
    finally { setWishBusy(false); }
  }

  function closeWishModal() { setShowWishModal(false); setWishDone(false); setWishName(""); setWishUrl(""); }

  if (!selectedKid) return null;

  const availableItems = storeItems.filter((i) => i.isAvailable);
  const shelves = providers
    .map((p) => ({ ...p, items: availableItems.filter((i) => i.providerId === p.providerId) }))
    .filter((s) => s.items.length > 0);

  const nav = (
    <nav className={styles.nav}>
      <button type="button" className={styles.navBtn} onClick={() => navigate("/kid")}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 11 L12 3 L21 11"/><path d="M5 10 V21 H19 V10"/></svg>
        Home
      </button>
      <button type="button" className={styles.navBtn} onClick={() => navigate("/kid/quest-log")}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 9 H21"/></svg>
        {theme.challengeLogTitle}
      </button>
      <button type="button" className={styles.navBtn} onClick={() => navigate("/kid/cashout")}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 21 L21 21"/><path d="M3 10 L12 3 L21 10"/></svg>
        {theme.pointBankTitle}
      </button>
      <button type="button" className={`${styles.navBtn} ${styles.navActive}`}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 7 L21 7 L20 20 L4 20 Z"/><path d="M9 7 V5 A3 3 0 0 1 15 5 V7"/></svg>
        Store
      </button>
    </nav>
  );

  return (
    <ThemeWrapper kid={selectedKid}>
    <div className={styles.page}>
      <PreviewBanner />
      <div className={styles.starbg} aria-hidden />

      <div className={own.storeHead}>
        <button type="button" className={styles.hudBack} onClick={() => navigate("/kid")}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18 L9 12 L15 6"/></svg>
        </button>
        <div className={own.storeTitle}>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 900, fontSize: 22 }}>🏪 Reward Store</div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "rgba(240,240,255,0.5)", marginTop: 2 }}>{`SPEND YOUR ${theme.pointNamePlural.toUpperCase()} ON REWARDS`}</div>
        </div>
        <div className={own.headerRight}>
          <button type="button" className={own.wishBtn} onClick={() => setShowWishModal(true)}>🌟 Wish</button>
          <button type="button" className={own.wishlistBtn} onClick={() => navigate("/kid/store/wishlist")}>
            ❤️ {wishlistItemIds.length}
          </button>
          {kid && (
            <div className={styles.starBal}>
              {theme.pointEmoji} {kid.pointBalance}
            </div>
          )}
        </div>
      </div>

      <div className={own.body}>
        {loadError && (
          <div style={{
            background: "rgba(255,107,107,0.12)", border: "1px solid rgba(255,107,107,0.3)",
            borderRadius: 12, padding: "10px 16px", fontSize: 13, color: "#ff8080",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            ⚠️ Couldn't load the store. Make sure the app server is running and try refreshing.
          </div>
        )}
        {loading && <div className={own.loading}>Loading store…</div>}

        {!loading && shelves.length === 0 && (
          <div className={own.emptyStore}>
            <div style={{ fontSize: 64, marginBottom: 8 }}>🏪</div>
            <div className={own.emptyTitle}>Store Opening Soon!</div>
            <div className={own.emptyDesc}>{`Your ${theme.parentRole} is setting up prizes. Keep earning ${theme.pointNamePlural.toLowerCase()}!`}</div>
          </div>
        )}

        {shelves.map((shelf) => (
          <div key={shelf.providerId} className={own.shelf}>
            <div className={own.shelfLabel}>{shelf.emoji} {shelf.name.toUpperCase()}</div>
            <div className={own.shelfItems}>
              {shelf.items.map((item) => {
                const canAfford = kid ? kid.pointBalance >= item.pointCost : false;
                const hearted   = wishlistItemIds.includes(item.itemId);
                return (
                  <div key={item.itemId} className={own.itemCard}>
                    <button type="button" className={`${own.heartBtn} ${hearted ? own.hearted : ""}`} onClick={() => toggleHeart(item.itemId)}>
                      {hearted ? "♥" : "♡"}
                    </button>
                    <div className={own.itemEmoji}>{item.emoji}</div>
                    <div className={own.itemName}>{item.name}</div>
                    {item.description && <div className={own.itemDesc}>{item.description}</div>}
                    <div className={own.itemCost}>{`${item.pointCost} ${theme.pointEmoji}`}</div>
                    {canAfford
                      ? <button type="button" className={own.requestBtn} onClick={() => setRequestItem(item)}>Request</button>
                      : <div className={own.needMore}>{`Not enough ${theme.pointNamePlural.toLowerCase()}`}</div>
                    }
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {nav}

      {requestItem && (
        <div className={styles.overlay}>
          <div className={own.confirmModal}>
            <button type="button" className={styles.overlayClose} onClick={() => setRequestItem(null)}>✕</button>
            <div className={own.confirmEmoji}>{requestItem.emoji}</div>
            <div className={own.confirmName}>{requestItem.name}</div>
            {requestItem.description && <div className={own.confirmDesc}>{requestItem.description}</div>}
            <div className={own.confirmCost}>
              {theme.pointEmoji} {requestItem.pointCost} {theme.pointNamePlural.toLowerCase()}
              <div className={own.confirmBalance}>Balance after: {theme.pointEmoji} {(kid?.pointBalance ?? 0) - requestItem.pointCost}</div>
            </div>
            <div className={own.confirmBtns}>
              <button type="button" className={styles.ctaGhost} onClick={() => setRequestItem(null)}>Cancel</button>
              <button type="button" className={styles.ctaGo} disabled={requesting} onClick={submitRequest}>
                {requesting ? "Requesting…" : `Request ${theme.pointEmoji} ${requestItem.pointCost}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {requestDone && (
        <div className={styles.overlay}>
          <div className={own.confirmModal}>
            <div className={own.confirmEmoji}>✅</div>
            <div className={own.confirmName}>Request sent!</div>
            <div className={own.confirmDesc}>Your {theme.parentRole} will review your request for {requestDone.name}.</div>
            <button type="button" className={styles.ctaGo} onClick={() => setRequestDone(null)} style={{ width: "100%" }}>Back to Store</button>
          </div>
        </div>
      )}

      {showWishModal && (
        <div className={styles.overlay}>
          <div className={own.confirmModal}>
            <button type="button" className={styles.overlayClose} onClick={closeWishModal}>✕</button>
            {wishDone ? (
              <>
                <div className={own.confirmEmoji}>🌟</div>
                <div className={own.confirmName}>Wish sent!</div>
                <div className={own.confirmDesc}>Your {theme.parentRole} will review it and may add it to the store.</div>
                <button type="button" className={styles.ctaGo} onClick={closeWishModal} style={{ width: "100%" }}>Done</button>
              </>
            ) : (
              <>
                <div className={own.confirmName}>🌟 Wish for something</div>
                <div className={own.confirmDesc}>Tell your {theme.parentRole} what you'd like. They decide if it can be added to the store.</div>
                <input className={own.wishInput} placeholder="What do you want?" value={wishName}
                  onChange={(e) => setWishName(e.target.value)} maxLength={80} />
                <input className={own.wishInput} placeholder="Link to it (optional, e.g. Amazon URL)" value={wishUrl}
                  onChange={(e) => setWishUrl(e.target.value)} />
                <div className={own.confirmBtns}>
                  <button type="button" className={styles.ctaGhost} onClick={closeWishModal}>Cancel</button>
                  <button type="button" className={styles.ctaGo} disabled={wishBusy || !wishName.trim()} onClick={submitWish}>
                    {wishBusy ? "Sending…" : "Send Wish"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
    </ThemeWrapper>
  );
}
